import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Sun, Shield, Bed, Bath, Square, ShoppingBag, Mountain, GraduationCap, TreePine, TrendingUp, Home, Heart, Car, Plane, Utensils } from 'lucide-react';
import { ContactCTA } from '../components/ContactCTA';
import { ListingsComingSoon } from '../components/ListingsComingSoon';
import { SEOHead } from '../components/SEOHead';
import { RelatedCommunities } from '../components/RelatedCommunities';
const placeholderListings = [{
  id: 1,
  price: '$520,000',
  address: '7834 Mesa Peak Dr',
  area: 'Mountains Edge',
  beds: 4,
  baths: 3,
  sqft: '2,300',
  image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2940&auto=format&fit=crop'
}, {
  id: 2,
  price: '$445,000',
  address: '9012 Blue Diamond Rd',
  area: 'Southwest',
  beds: 3,
  baths: 2,
  sqft: '1,800',
  image: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?q=80&w=2792&auto=format&fit=crop'
}, {
  id: 3,
  price: '$675,000',
  address: '2145 Cactus Ridge Way',
  area: 'Rhodes Ranch',
  beds: 4,
  baths: 3.5,
  sqft: '2,900',
  image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=2950&auto=format&fit=crop'
}];
export function SouthwestPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="bg-white" data-id="element-1627">
      <SEOHead title="Southwest Las Vegas Real Estate & Homes for Sale | Jenny Gong" description="Explore homes for sale in Southwest Las Vegas, a rapidly growing area offering modern developments, custom homes, and stunning mountain views." keywords="Southwest Las Vegas real estate, Southwest Las Vegas homes for sale, Mountains Edge real estate, Rhodes Ranch homes for sale" canonicalUrl="https://jennygong.com/southwest" ogImage="https://images.unsplash.com/photo-1600585154526-990dced4db0d?q=80&w=2787&auto=format&fit=crop" data-id="element-1628" />
      <section className="relative h-[70vh] min-h-[600px] w-full flex items-center justify-center overflow-hidden" data-id="element-1629">
        <div className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1600585154526-990dced4db0d?q=80&w=2787&auto=format&fit=crop")'
      }} data-id="element-1630">
          <div className="absolute inset-0 bg-charcoal/40 mix-blend-multiply" data-id="element-1631"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/60 via-transparent to-charcoal/80" data-id="element-1632"></div>
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto mt-20" data-id="element-1633">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1
        }} data-id="element-1634">
            <span className="text-gold uppercase tracking-[0.3em] text-sm font-medium mb-6 block" data-id="element-1635">
              Community Spotlight
            </span>
            <h1 className="text-5xl md:text-7xl text-white font-serif mb-6 leading-tight" data-id="element-1636">
              Southwest Las Vegas Homes for Sale
            </h1>
            <p className="text-lg md:text-xl text-white/90 font-light max-w-2xl mx-auto" data-id="element-1637">
              One of the fastest-growing areas in the valley with modern
              developments and stunning mountain views.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 px-6 bg-white" data-id="element-1638">
        <div className="max-w-7xl mx-auto" data-id="element-1639">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1640">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-1641">
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-6" data-id="element-1642">
                The Future of{' '}
                <span className="italic text-gold" data-id="element-1643">Las Vegas Living</span>
              </h2>
              <div className="space-y-4 text-charcoal-light font-light leading-relaxed text-lg" data-id="element-1644">
                <p data-id="element-1645">
                  Southwest Las Vegas has exploded in growth over the past
                  decade, transforming from open desert into one of the most
                  exciting residential areas in the valley. With brand-new
                  master-planned communities, modern architecture, and some of
                  the best mountain views in Vegas, it's attracting buyers who
                  want new construction at exceptional value.
                </p>
                <p data-id="element-1646">
                  The area is anchored by communities like Mountain's Edge,
                  Rhodes Ranch, and the rapidly developing areas along Blue
                  Diamond Road. Homes range from affordable $350K new builds to
                  stunning $800K+ custom homes with panoramic views of the
                  Spring Mountains. Neighboring{' '}
                  <Link to="/spring-valley" className="text-gold hover:underline" data-id="element-1647">
                    Spring Valley
                  </Link>{' '}
                  and{' '}
                  <Link to="/summerlin" className="text-gold hover:underline" data-id="element-1648">
                    Summerlin
                  </Link>{' '}
                  offer additional luxury options nearby.
                </p>
                <p data-id="element-1649">
                  Southwest Las Vegas also benefits from proximity to the new
                  Raiders Allegiant Stadium, the Las Vegas Strip, and easy
                  freeway access via the 215 Beltway and I-15.
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="grid grid-cols-2 gap-6" data-id="element-1650">
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-1651">
                <TrendingUp className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1652" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-1653">#1</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-1654">
                  Growth Area
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-1655">
                <Home className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1656" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-1657">New</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-1658">
                  Construction
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-1659">
                <Mountain className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1660" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-1661">Epic</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-1662">
                  Mountain Views
                </p>
              </div>
              <div className="bg-warmGray p-8 text-center border border-warmGray-dark" data-id="element-1663">
                <MapPin className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1664" />
                <h4 className="text-3xl font-serif text-charcoal mb-2" data-id="element-1665">15m</h4>
                <p className="text-sm text-charcoal-light uppercase tracking-wider" data-id="element-1666">
                  To The Strip
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Communities */}
      <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-1667">
        <div className="max-w-7xl mx-auto" data-id="element-1668">
          <div className="text-center mb-16" data-id="element-1669">
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1670">
              Master-Planned Living
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-1671">
              Top Communities
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1672">
            {[{
            name: "Mountain's Edge",
            price: '$380K - $650K',
            desc: "One of Vegas's most popular master-planned communities with 1,000+ acres of parks, trails, and a stunning exploration peak trail system.",
            img: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2940&auto=format&fit=crop'
          }, {
            name: 'Rhodes Ranch',
            price: '$400K - $800K',
            desc: 'A guard-gated golf course community featuring a Ted Robinson-designed course, resort-style pools, and elegant homes.',
            img: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=2950&auto=format&fit=crop'
          }, {
            name: 'Inspirada',
            price: '$350K - $550K',
            desc: 'A vibrant newer community in the southwest corridor with modern homes, community pools, and excellent parks.',
            img: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?q=80&w=2792&auto=format&fit=crop'
          }].map((n, idx) => <motion.div key={n.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white p-6 shadow-sm border border-warmGray-dark" data-id="element-1673">
                <img src={n.img} alt={n.name} className="w-full h-48 object-cover mb-4" data-id="element-1674" />
                <div className="flex justify-between items-start mb-3" data-id="element-1675">
                  <h3 className="text-xl font-serif text-charcoal" data-id="element-1676">{n.name}</h3>
                  <span className="text-xs font-medium text-gold bg-gold/10 px-2 py-1 rounded whitespace-nowrap" data-id="element-1677">
                    {n.price}
                  </span>
                </div>
                <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1678">
                  {n.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Nearby */}
      <section className="py-24 px-6 bg-white" data-id="element-1679">
        <div className="max-w-7xl mx-auto" data-id="element-1680">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-1681">
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1682">
              Life in the Southwest
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-1683">What's Nearby</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1684">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="group" data-id="element-1685">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1686">
                <img src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=2842&auto=format&fit=crop" alt="Red Rock" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1687" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1688">
                Red Rock Canyon
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-1689">
                Just minutes from Southwest Las Vegas, offering world-class
                hiking, rock climbing, and the iconic 13-mile scenic drive.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="group" data-id="element-1690">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1691">
                <img src="https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?q=80&w=2905&auto=format&fit=crop" alt="Stadium" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1692" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1693">
                Allegiant Stadium
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-1694">
                Home of the Las Vegas Raiders and host to major concerts and
                events, just a short drive via I-15.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="group" data-id="element-1695">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1696">
                <img src="https://images.unsplash.com/photo-1582719508461-905c673771fd?q=80&w=2825&auto=format&fit=crop" alt="Durango Casino" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1697" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1698">
                Durango Casino & Resort
              </h3>
              <p className="text-charcoal-light font-light text-sm" data-id="element-1699">
                The newest luxury destination in the Southwest, featuring
                world-class dining, upscale entertainment, and a
                state-of-the-art sportsbook.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Education */}
      <section className="py-24 bg-warmGray px-6 border-t border-warmGray-dark" data-id="element-1700">
        <div className="max-w-7xl mx-auto" data-id="element-1701">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-1702">
            <GraduationCap className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1703" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1704">
              Education
            </span>
            <h2 className="text-4xl font-serif text-charcoal mb-6" data-id="element-1705">
              Top-Rated Schools
            </h2>
            <p className="text-charcoal-light font-light max-w-3xl mx-auto text-lg leading-relaxed" data-id="element-1706">
              Southwest Las Vegas boasts some of the newest and highest-rated
              educational facilities in the Clark County School District,
              alongside prestigious private academies.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1707">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white p-8 border border-warmGray-dark" data-id="element-1708">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-1709">
                Elementary & Middle
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-1710">
                <li data-id="element-1711">• Elise L. Wolff Elementary (Blue Ribbon)</li>
                <li data-id="element-1712">• Carolyn S. Reedom Elementary</li>
                <li data-id="element-1713">• John C. Vanderburg Elementary</li>
                <li data-id="element-1714">• Myrtle Tate Elementary</li>
                <li data-id="element-1715">• Lois & Jerry Tarkanian Middle</li>
                <li data-id="element-1716">• Lawrence & Heidi Canarelli Middle</li>
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white p-8 border border-warmGray-dark" data-id="element-1717">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-1718">
                High Schools
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-1719">
                <li data-id="element-1720">• Durango High School</li>
                <li data-id="element-1721">• Desert Oasis High School</li>
                <li data-id="element-1722">• Sierra Vista High School</li>
                <li data-id="element-1723">• Spring Valley High School</li>
                <li className="pt-4 italic text-xs text-gold" data-id="element-1724">
                  Known for excellent magnet programs and advanced placement
                  opportunities.
                </li>
              </ul>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-white p-8 border border-warmGray-dark" data-id="element-1725">
              <h3 className="text-xl font-serif text-charcoal mb-4 border-b border-gold/20 pb-4" data-id="element-1726">
                Private & Charter
              </h3>
              <ul className="space-y-3 text-charcoal-light font-light text-sm" data-id="element-1727">
                <li data-id="element-1728">• Bishop Gorman High School (Elite Catholic Prep)</li>
                <li data-id="element-1729">• Faith Lutheran Middle & High School</li>
                <li data-id="element-1730">• Pinecrest Academy (Multiple Campuses)</li>
                <li data-id="element-1731">• Doral Academy</li>
                <li data-id="element-1732">• Somerset Academy</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Parks & Recreation */}
      <section className="py-24 px-6 bg-white" data-id="element-1733">
        <div className="max-w-7xl mx-auto" data-id="element-1734">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-1735">
            <TreePine className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1736" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1737">
              Outdoor Living
            </span>
            <h2 className="text-4xl font-serif text-charcoal" data-id="element-1738">
              Parks & Recreation
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1739">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="group" data-id="element-1740">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1741">
                <img src="https://images.unsplash.com/photo-1519331379826-f10be5486c6f?q=80&w=2940&auto=format&fit=crop" alt="Exploration Peak Park" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1742" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1743">
                Exploration Peak Park
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1744">
                The crown jewel of Mountain's Edge. This unique park features a
                prominent mountain peak with hiking trails leading to panoramic
                360° views of the Las Vegas Valley, plus an Old West-themed
                playground and water play area.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="group" data-id="element-1745">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1746">
                <img src="https://images.unsplash.com/photo-1584467735815-f778f274e296?q=80&w=2787&auto=format&fit=crop" alt="Mountain's Edge Regional Park" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1747" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1748">
                Mountain's Edge Regional Park
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1749">
                A massive 100+ acre facility featuring lighted sports fields,
                basketball courts, a sprawling dog park, outdoor fitness
                equipment, and extensive walking paths that connect the entire
                community.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="group" data-id="element-1750">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1751">
                <img src="https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?q=80&w=2948&auto=format&fit=crop" alt="Desert Breeze Park" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1752" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1753">
                Desert Breeze Park
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1754">
                One of the largest parks in the county, offering a massive
                community center, indoor/outdoor aquatic facility, skate park,
                dog runs, and numerous baseball and soccer fields.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="group" data-id="element-1755">
              <div className="relative h-64 mb-6 overflow-hidden" data-id="element-1756">
                <img src="https://images.unsplash.com/photo-1472214103451-9374bd1c798e?q=80&w=2940&auto=format&fit=crop" alt="Trail System" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1757" />
              </div>
              <h3 className="text-2xl font-serif text-charcoal mb-3" data-id="element-1758">
                Extensive Trail Systems
              </h3>
              <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1759">
                The Southwest is designed for active living, with miles of paved
                and unpaved trails weaving through neighborhoods, perfect for
                cycling, jogging, and evening strolls.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Shopping & Dining */}
      <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-1760">
        <div className="max-w-7xl mx-auto" data-id="element-1761">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1762">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-2 lg:order-1" data-id="element-1763">
              <div className="relative h-[600px]" data-id="element-1764">
                <img src="https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=2874&auto=format&fit=crop" alt="Dining" className="w-full h-full object-cover" data-id="element-1765" />
                <div className="absolute inset-0 border border-gold/30 m-4 pointer-events-none" data-id="element-1766"></div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="order-1 lg:order-2" data-id="element-1767">
              <ShoppingBag className="w-10 h-10 text-gold mb-6" data-id="element-1768" />
              <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1769">
                Lifestyle & Culinary
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-charcoal mb-8" data-id="element-1770">
                Shopping & Dining
              </h2>

              <div className="space-y-8" data-id="element-1771">
                <div data-id="element-1772">
                  <h4 className="font-serif text-xl text-charcoal mb-2 flex items-center gap-2" data-id="element-1773">
                    <Utensils className="w-5 h-5 text-gold" data-id="element-1774" />
                    The Gramercy & Durango Corridor
                  </h4>
                  <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1775">
                    Upscale mixed-use developments offering boutique fitness,
                    trendy cafes, and high-end dining experiences without having
                    to visit the Strip.
                  </p>
                </div>

                <div data-id="element-1776">
                  <h4 className="font-serif text-xl text-charcoal mb-2 flex items-center gap-2" data-id="element-1777">
                    <ShoppingBag className="w-5 h-5 text-gold" data-id="element-1778" />
                    Blue Diamond Crossing
                  </h4>
                  <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1779">
                    A massive retail hub providing every daily convenience
                    imaginable, from major big-box retailers and grocery stores
                    to dozens of casual dining options.
                  </p>
                </div>

                <div data-id="element-1780">
                  <h4 className="font-serif text-xl text-charcoal mb-2 flex items-center gap-2" data-id="element-1781">
                    <Utensils className="w-5 h-5 text-gold" data-id="element-1782" />
                    Spring Mountain Road (Chinatown)
                  </h4>
                  <p className="text-charcoal-light font-light text-sm leading-relaxed" data-id="element-1783">
                    Just minutes north, this famous 3+ mile stretch is
                    recognized as one of the best Asian food corridors in
                    America, offering world-class authentic cuisine.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Healthcare */}
      <section className="py-24 px-6 bg-white" data-id="element-1784">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1785">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-1786">
            <Heart className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1787" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1788">
              Wellness
            </span>
            <h2 className="text-4xl font-serif text-charcoal mb-6" data-id="element-1789">
              World-Class Healthcare
            </h2>
            <p className="text-charcoal-light font-light leading-relaxed text-lg mb-8" data-id="element-1790">
              Residents of the Southwest enjoy immediate access to top-tier
              medical facilities. The area is anchored by{' '}
              <strong data-id="element-1791">Southern Hills Hospital & Medical Center</strong>, a
              full-service facility known for its advanced emergency care,
              orthopedics, and neurology departments.
            </p>
            <p className="text-charcoal-light font-light leading-relaxed text-lg" data-id="element-1792">
              Additionally, the nearby <strong data-id="element-1793">Spring Valley Hospital</strong>{' '}
              and dozens of specialized clinics, urgent care centers, and
              wellness spas along the Durango and Blue Diamond corridors ensure
              comprehensive care is always minutes away.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Transportation */}
      <section className="py-24 bg-charcoal px-6 text-white" data-id="element-1794">
        <div className="max-w-7xl mx-auto" data-id="element-1795">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-1796">
            <MapPin className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-1797" />
            <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-1798">
              Connectivity
            </span>
            <h2 className="text-4xl font-serif mb-6" data-id="element-1799">
              Transportation & Access
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1800">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-1801">
              <Car className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1802" />
              <h4 className="text-xl font-serif mb-2" data-id="element-1803">215 Beltway</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-1804">
                The main artery connecting the Southwest to Summerlin,
                Henderson, and the airport seamlessly.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-1805">
              <MapPin className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1806" />
              <h4 className="text-xl font-serif mb-2" data-id="element-1807">I-15 Access</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-1808">
                Quick access to the Las Vegas Strip, Downtown, and the main
                route to Southern California.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-1809">
              <Plane className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1810" />
              <h4 className="text-xl font-serif mb-2" data-id="element-1811">Harry Reid Airport</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-1812">
                Approximately a 15-20 minute drive via the 215 Beltway, making
                travel incredibly convenient.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="bg-white/5 p-8 border border-white/10 text-center" data-id="element-1813">
              <MapPin className="w-8 h-8 text-gold mx-auto mb-4" data-id="element-1814" />
              <h4 className="text-xl font-serif mb-2" data-id="element-1815">Major Corridors</h4>
              <p className="text-white/60 font-light text-sm" data-id="element-1816">
                Blue Diamond Rd and Durango Dr serve as massive, recently
                expanded thoroughfares for local transit.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <RelatedCommunities currentCommunity="Southwest" data-id="element-1817" />

      <ListingsComingSoon area="Southwest Las Vegas" data-id="element-1818" />

      <ContactCTA data-id="element-1819" />
    </main>;
}